import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { AHIcon, AHText, Box, useTheme, useTime } from "arkh";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "react-native-qrcode-svg";
import { graphql, useSubscription } from "react-relay";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { FormattedMessage } from "react-intl";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  SharedMobileDeviceAuthRequestStatus,
  UserAdminCreateGroupDeviceSubscription,
} from "./__generated__/UserAdminCreateGroupDeviceSubscription.graphql";
import { DateTime } from "luxon";
import { UserAdminCreateGroupDeviceAcceptRequestScreen } from "./UserAdminCreateGroupDeviceAcceptRequest";

function InnerUserAdminCreateGroupDeviceScreen(props: {
  deviceCode: string;
  openDeviceEdit: (id: string) => void;
  expiresAt: string;
  onExpired: () => void;
}) {
  const theme = useTheme();
  const [acceptConfig, setAcceptConfig] = useState<{
    deviceCode: string;
    status: SharedMobileDeviceAuthRequestStatus;
    nameSuggestion: string;
  } | null>(null);
  const subscriptionConfig = useMemo(
    () => ({
      subscription: graphql`
        subscription UserAdminCreateGroupDeviceSubscription(
          $deviceCode: String!
        ) {
          sharedMobileDeviceAuthRequestUpdated(deviceCode: $deviceCode) {
            deviceCode
            status
            nameSuggestion
          }
        }
      `,
      variables: {
        deviceCode: props.deviceCode,
      },
      onNext: (data: any) => {
        if (data?.sharedMobileDeviceAuthRequestUpdated) {
          setAcceptConfig(data.sharedMobileDeviceAuthRequestUpdated);
        }
      },
    }),
    [props]
  );

  useSubscription<UserAdminCreateGroupDeviceSubscription>(subscriptionConfig);

  const s = useTime(1000);

  const expiresAt = useMemo(() => {
    return DateTime.fromISO(props.expiresAt);
  }, [props.expiresAt]);

  const diff = useMemo(() => {
    return expiresAt.diff(s);
  }, [expiresAt, s]);

  const hasExpired = useMemo(() => {
    return diff.as("seconds") <= 0;
  }, [diff]);
  const expiresIn = useMemo(() => {
    return diff.shiftTo("minutes", "seconds").toHuman({
      unitDisplay: "long",
      listStyle: "long",
      maximumFractionDigits: 0,
    });
  }, [diff]);

  const onExpired = props.onExpired;
  useEffect(() => {
    if (hasExpired) {
      onExpired();
    }
  }, [hasExpired, onExpired]);

  if (acceptConfig) {
    return (
      <UserAdminCreateGroupDeviceAcceptRequestScreen
        deviceCode={acceptConfig.deviceCode}
        nameSuggestion={acceptConfig.nameSuggestion}
        onAccept={props.openDeviceEdit}
        onReject={onExpired}
      />
    );
  }

  return (
    <Box
      space="xlarge"
      color="surface-primary"
      style={{ flex: 1, justifyContent: "flex-start", alignItems: "center" }}
    >
      <AHIcon type="scan" size="large" color="darkerText" />
      <AHText type="title" spaceTop="large" spaceBottom="small">
        <FormattedMessage
          defaultMessage="Scan with shared device"
          description="Title for the shared device scan screen"
        />
      </AHText>
      <AHText type="subtitle" color="text-secondary" spaceBottom="xlarge">
        <FormattedMessage
          defaultMessage={`Open the Storesprint app on the shared device and select "Log in as shared device"`}
          description="Description for the shared device scan screen"
        />
      </AHText>

      <Box
        shadow="strong"
        cornerRadius="large"
        color="surface-primary"
        space="large"
      >
        <QRCode
          size={200}
          backgroundColor={theme.background}
          color={theme.darkerText}
          value={props.deviceCode}
        />
      </Box>
      <FormattedDeviceCode deviceCode={props.deviceCode} />

      <AHText
        spaceTop="large"
        type="subtitle"
        color="text-secondary"
        spaceBottom="xlarge"
        textAlign="center"
      >
        <FormattedMessage
          defaultMessage={`Expires in {expiresIn}`}
          description="UserAdminCreateGroupDeviceScreen Description for the shared device scan screen"
          values={{
            expiresIn,
          }}
        />
      </AHText>
    </Box>
  );
}

function FormattedDeviceCode(props: { deviceCode: string }) {
  const dc = props.deviceCode.split("");
  return (
    <AHText type="title" color="darkerText" spaceTop="small">
      {dc.map((d, index) => (
        <Box key={index} spaceHorizontal="xxsmall">
          <Box
            space="small"
            shadow="weak"
            cornerRadius="small"
            color="surface-primary"
          >
            <AHText
              spaceHorizontal="xxsmall"
              key={index}
              type="subtitle"
              color="darkerText"
            >
              {d}
            </AHText>
          </Box>
        </Box>
      ))}
    </AHText>
  );
}

function UserAdminCreateGroupDeviceScreen(props: {
  route: RouteProp<ParamList, "UserAdminCreateGroupDeviceScreen">;
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "UserAdminCreateGroupDeviceScreen">,
    UserLoggedInNavigatorNavigationProp<"UserAdminCreateGroupDevice">
  >;
}) {
  const onExpired = useCallback(() => {
    props.navigation.goBack();
  }, [props.navigation]);

  return (
    <InnerUserAdminCreateGroupDeviceScreen
      deviceCode={props.route.params.deviceCode}
      expiresAt={props.route.params.expiresAt}
      onExpired={onExpired}
      openDeviceEdit={(id) =>
        props.navigation.replace("GroupDeviceEdit", { id })
      }
    />
  );
}

type ParamList = {
  UserAdminCreateGroupDeviceScreen: { deviceCode: string; expiresAt: string };
};

const Stack = createNativeStackNavigator<ParamList>();

export const UserAdminCreateGroupDevice = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"UserAdminCreateGroupDevice">) => {
    const getOptions = useNavigationHeaderOptions();

    return (
      <Stack.Navigator>
        <Stack.Screen
          name="UserAdminCreateGroupDeviceScreen"
          component={UserAdminCreateGroupDeviceScreen}
          initialParams={{
            deviceCode: props.route.params.deviceCode,
            expiresAt: props.route.params.expiresAt,
          }}
          options={getOptions({
            type: "CLOSE",
            onClose: () => {
              props.navigation.goBack();
            },
            title: "",
          })}
        />
      </Stack.Navigator>
    );
  }
);
